import { Box, Button, ExpandableSection, Grid, Link } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Event, EventStatus, getEventListStatus } from '../../../../types/Event';
import { i18nKeys } from '../../../../utils/i18n.utils';
import LabShutoffStatusBadge from '../../../common/LabShutoffStatusBadge';
import LabStatusCountdown from '../../eventsCommon/labStatusCountdown/LabStatusCountdown';
import SecretKey from '../../../common/SecretKey';
import { MY_JAM_PREFIX } from '@/src/constants/api';
import { useApi } from '@/src/store/api.context';
import { useUser } from '@/src/store/user.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { GetEventByNameResponse } from '@/src/api';
import { copyToClipboard } from '@/src/utils/copy-to-clipboard';
import { EventPrivacyType } from '@/src/types/EventPrivacyType';
import './Access.scss';

interface AccessProps {
  event: Event | undefined;
  onAddFlashbar: (error: string) => void;
}

const Access: React.FC<AccessProps> = ({ event }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { addErrorFlashbar, addSuccessFlashbar } = useFlashbars();

  // create URL to append with path
  const hostName = window.location.hostname;
  const protocol = window.location.protocol;
  let hostURL = `${protocol}//${hostName}`;
  const port = window.location.port;
  hostURL = hostName === 'localhost' || hostName === '127.0.0.1' ? `${hostURL}:${port}` : hostURL; // create URL based on local and live URL port

  const prefix = `/${MY_JAM_PREFIX}`;

  const URLLink = `${prefix}${event?.name}`;
  const URL = `${hostURL}${URLLink}`;

  const { eventsApi } = useApi();

  const [isCreatingTestEvent, setIsCreatingTestEvent] = useState(false);
  const [createTestEventSuccess, setCreateTestEventSuccess] = useState(false);

  const handleCreateTestEvent = async () => {
    setIsCreatingTestEvent(true);
    try {
      await eventsApi.createTestEvent(event);
      setCreateTestEventSuccess(true);
      window.location.reload();
    } catch (error) {
      setCreateTestEventSuccess(false);
      addErrorFlashbar('Failed to create test event');
      throw error;
    }
  };

  const [testEvent, setTestEvent] = useState<GetEventByNameResponse | undefined>();

  useEffect(() => {
    const fetchTestEvent = async () => {
      if (event && event.testCloneEventName) {
        try {
          const response = await eventsApi.getEvent(event.testCloneEventName);
          setTestEvent(response);
        } catch (error) {
          addErrorFlashbar('Failed to get test event');
        }
      }
    };

    if (event && event.testCloneEventName) {
      void fetchTestEvent();
    }
  }, []);

  const copySecretKey = (secretKey: string) => {
    copyToClipboard(secretKey);
    addSuccessFlashbar(t(i18nKeys.events.eventDetails.labels.secretKey.message));
  };

  const copyEventLink = (eventUrl: string) => {
    copyToClipboard(eventUrl);
    addSuccessFlashbar(t(i18nKeys.clipboard.eventLinkCopied));
  };

  const getTestEventUrl = (ev: Event | undefined, full: boolean) => {
    if (ev && ev.testCloneEventName) {
      const path = `${MY_JAM_PREFIX}/${ev?.testCloneEventName ?? ''}`;
      return full ? hostURL + path : path;
    }
    return '';
  };

  const gridDefinition = [{ colspan: 3 }, { colspan: 9 }];

  return (
    <React.Fragment>
      {event && event?.approved && (
        <ExpandableSection
          variant="container"
          defaultExpanded
          headerText={t(i18nKeys.events.eventDetails.headers.access)}
        >
          <div style={{ paddingBottom: '15px' }} className="container-table-row">
            {t(i18nKeys.events.eventDetails.messages.eventAccess)}
            <div style={{ paddingTop: '5px' }}>
              <Box>
                1. <strong>{t(i18nKeys.events.eventDetails.headers.link)}:</strong>{' '}
                {t(i18nKeys.events.eventDetails.messages.eventAccessOption1)}
                <br />
              </Box>
              {event?.eventCode && (
                <Box>
                  2. <strong>{t(i18nKeys.events.eventDetails.headers.secretKey)}:</strong>{' '}
                  {t(i18nKeys.events.eventDetails.messages.eventAccessOption2)}
                </Box>
              )}
            </div>
          </div>
          <div className="event-access__grid">
            {/* Left side */}
            <div className="container-table-header cell-1-1">
              <Grid gridDefinition={gridDefinition}>
                <div className="event-access__type-header-cell">
                  <strong>{t(i18nKeys.events.eventDetails.headers.eventType.header)}</strong>
                </div>
                {!event?.test ? (
                  <div>{t(i18nKeys.events.eventDetails.labels.liveEvent)}</div>
                ) : (
                  <div>{t(i18nKeys.events.eventDetails.labels.testEvent)}</div>
                )}
              </Grid>
            </div>
            <div className="cell-1-2 container-table-row container-table-row1">
              <Grid gridDefinition={gridDefinition}>
                <React.Fragment>
                  <div>
                    <strong>{t(i18nKeys.events.eventDetails.headers.link)}</strong>
                  </div>
                  <div className="event-access__live-url-cell">
                    <Link external href={URLLink} data-testid="event-access__live-event-link">
                      {URL}
                    </Link>
                    <div style={{ paddingTop: '10px' }}>
                      <Button onClick={() => copyEventLink(URL)} data-testid="event-access__copy-live-event-url-btn">
                        {t(i18nKeys.events.eventDetails.buttons.copyEventLink)}
                      </Button>
                    </div>
                  </div>
                </React.Fragment>
              </Grid>
              {event?.eventPrivacyType === EventPrivacyType.PRIVATE_INVITE && (
                <Box variant="div" margin={{ top: 'l' }}>
                  {t(i18nKeys.events.eventDetails.messages.privateEventParticipantManagement)}
                </Box>
              )}
            </div>
            {event?.eventPrivacyType === EventPrivacyType.PRIVATE_CODE && event?.eventCode && (
              <div className="container-table-row cell-1-3">
                <Grid gridDefinition={gridDefinition}>
                  <React.Fragment>
                    <div>
                      <strong>{t(i18nKeys.events.eventDetails.headers.secretKey)}</strong>
                    </div>
                    <div>
                      <SecretKey secretKey={event?.eventCode} />
                      <div style={{ paddingTop: '10px' }}>
                        <Button onClick={() => copySecretKey(event?.eventCode ?? '')}>
                          {t(i18nKeys.events.eventDetails.buttons.copySecretKey)}
                        </Button>
                      </div>
                    </div>
                  </React.Fragment>
                </Grid>
              </div>
            )}
            <div className="container-table-row cell-1-4">
              <Grid gridDefinition={gridDefinition}>
                <React.Fragment>
                  <div>
                    <strong>{t(i18nKeys.events.eventDetails.headers.status)}</strong>
                  </div>
                  <div>
                    <LabStatusCountdown
                      isTest={event?.test}
                      includeSeconds={false}
                      inline
                      status={getEventListStatus(event)}
                      labStartTime={event.labStartTime}
                      labEndTime={event.labEndTime}
                    />
                    <span style={{ marginRight: '20px' }} />
                    <LabShutoffStatusBadge
                      challengeDescriptors={event.challengeDescriptors}
                      shutoffStatus={event.shutoffStatus}
                      testEventShutoffStatus={event.testEventShutoffStatus}
                      test={event?.test}
                    />
                  </div>
                </React.Fragment>
              </Grid>
            </div>
            {/* Right side */}
            {event?.testCloneEventName && !event.test ? (
              <>
                <div className="container-table-header cell-2-1">
                  <Grid gridDefinition={[{ colspan: 12 }]}>
                    <div className="event-access__type-header-cell">
                      {t(i18nKeys.events.eventDetails.labels.testEvent)}
                    </div>
                  </Grid>
                </div>

                <div className="cell-2-2 container-table-row">
                  <Grid gridDefinition={[{ colspan: 10 }]}>
                    <React.Fragment>
                      <Link external href={getTestEventUrl(event, false)} data-testid="event-access__test-event-link">
                        {getTestEventUrl(event, true)}
                      </Link>
                      <div style={{ marginTop: '-10px' }}>
                        <Button
                          data-testid="event-access__test-event-copy-url-btn"
                          onClick={() => copyEventLink(getTestEventUrl(event, true))}
                        >
                          {t(i18nKeys.events.eventDetails.buttons.copyEventLink)}
                        </Button>
                      </div>
                    </React.Fragment>
                  </Grid>
                </div>
                {testEvent?.event?.eventPrivacyType === EventPrivacyType.PRIVATE_CODE &&
                  testEvent?.event?.eventCode && (
                    <div className="cell-2-3 container-table-row">
                      <Grid gridDefinition={[{ colspan: 12 }]}>
                        <div>
                          <SecretKey secretKey={testEvent?.event.eventCode} />
                          <div style={{ paddingTop: '10px' }}>
                            <Button
                              data-testid="event-access__test-event-copy-secret-btn"
                              onClick={() => copySecretKey(testEvent?.event.eventCode ?? '')}
                            >
                              {t(i18nKeys.events.eventDetails.buttons.copySecretKey)}
                            </Button>
                          </div>
                        </div>
                      </Grid>
                    </div>
                  )}
                <div className="cell-2-4 container-table-row">
                  <Grid gridDefinition={[{ colspan: 12 }]}>
                    <React.Fragment>
                      <div>
                        <LabStatusCountdown
                          isTest
                          includeSeconds={false}
                          status={getEventListStatus(testEvent?.event)}
                          labStartTime={testEvent?.event.labStartTime ?? ''}
                          labEndTime={testEvent?.event.labEndTime ?? ''}
                          inline
                        />
                        <span style={{ marginRight: '20px' }} />
                        <LabShutoffStatusBadge
                          challengeDescriptors={testEvent?.event.challengeDescriptors}
                          shutoffStatus={testEvent?.event.testEventShutoffStatus}
                          testEventShutoffStatus={testEvent?.event.testEventShutoffStatus}
                          test
                        />
                      </div>
                    </React.Fragment>
                  </Grid>
                </div>
              </>
            ) : user &&
              !event.testCloneEventName &&
              !createTestEventSuccess &&
              !event.test &&
              event.isEventOwner(user) ? (
              <>
                <div className="container-table-header cell-2-1">
                  <Grid gridDefinition={[{ colspan: 12 }]}>
                    <div className="event-access__type-header-cell">
                      {t(i18nKeys.events.eventDetails.labels.testEvent)}
                    </div>
                  </Grid>
                </div>

                <div className="container-table-row cell-2-2">
                  <Grid gridDefinition={[{ colspan: 12 }]}>
                    <div className="height-forcer">
                      {t(i18nKeys.events.eventDetails.messages.testEvent)}
                      <div style={{ padding: '1rem 0 10px 0' }}>
                        <Button
                          data-testid="event-access__create-test-event-btn"
                          onClick={() => {
                            void (async () => {
                              try {
                                setIsCreatingTestEvent(true);
                                await handleCreateTestEvent();
                                setIsCreatingTestEvent(false);
                              } catch (error) {
                                setIsCreatingTestEvent(false);
                              }
                            })();
                          }}
                          disabled={isCreatingTestEvent || event.status !== EventStatus.NOT_STARTED}
                        >
                          {isCreatingTestEvent
                            ? t(i18nKeys.events.eventDetails.buttons.creatingTestEvent)
                            : t(i18nKeys.events.eventDetails.buttons.createTestEvent)}
                        </Button>
                      </div>
                    </div>
                  </Grid>
                </div>
                <div className="cell-2-3 container-table-row" />
                <div className="cell-2-4 container-table-row" />
              </>
            ) : null}
          </div>
        </ExpandableSection>
      )}
    </React.Fragment>
  );
};

export default Access;
